// Client for the workspace-facing Car Wrap Shops directory listing endpoints
// (server/src/modules/directory-listing). Phase 1 (search, GET /listing,
// POST /claim-requests) is live on main; PATCH /listing and POST
// /listing/media are the phase 2 server half being built in parallel with
// this client, so the shapes below follow the fixed API contract rather
// than an existing server response.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspacePath } from '@wrapforce/shared/api-client';
import type { QuoteService } from '@wrapforce/shared/constants';
import { api } from '@/lib/api';

// Same four-key set directory_shops.services and the quote widget use
// (packages/shared/src/constants.ts QUOTE_SERVICES).
export type DirectoryServiceKey = QuoteService;

// Mirrors directory_shops.hours (directory/src/pages/claim/[slug]/manage.astro):
// one tuple of [open, close] "HH:MM" strings per day, or an empty/missing
// array meaning the shop is closed that day.
export type DirectoryHours = Record<string, [string, string][]>;

export interface DirectoryShopSearchResult {
  id: string;
  name: string;
  city: string;
  region: string;
  slug: string;
  owned: boolean;
}

export interface DirectoryListing {
  shopId: string;
  name: string;
  slug: string;
  city: string;
  region: string;
  verifiedAt: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  services: DirectoryServiceKey[];
  hours: DirectoryHours;
  logoUrl: string | null;
  bannerUrl: string | null;
}

export interface DirectoryPendingRequest {
  id: string;
  shopId: string;
  shopName: string;
  // Assumed addition over the phase 1 shape so the pending state can link to
  // the public listing while it's under review; the link is simply omitted
  // if the server doesn't send it.
  shopSlug?: string | null;
  note: string | null;
  createdAt: string;
}

export interface DirectoryListingGetResponse {
  listing: DirectoryListing | null;
  pendingRequest: DirectoryPendingRequest | null;
}

export interface DirectoryListingUpdateInput {
  services?: DirectoryServiceKey[];
  hours?: DirectoryHours;
  email?: string;
  phone?: string;
  website?: string;
}

export function useDirectoryShopSearch(workspaceId: string | null, q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: [workspaceId, 'directory-shop-search', trimmed],
    queryFn: () =>
      api.get<{ items: DirectoryShopSearchResult[] }>(workspacePath(workspaceId!, '/directory/shops/search'), {
        query: { q: trimmed },
      }),
    enabled: !!workspaceId && trimmed.length >= 3,
    staleTime: 15_000,
  });
}

export function useDirectoryListing(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'directory-listing'],
    queryFn: () => api.get<DirectoryListingGetResponse>(workspacePath(workspaceId!, '/directory/listing')),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

// §0089 - claim requests now carry a required identity/ownership document
// alongside the business email and optional note, so the request goes over
// FormData rather than a JSON body (mirrors useUploadDirectoryListingMedia
// below).
export interface CreateDirectoryClaimRequestInput {
  shopId: string;
  businessEmail: string;
  evidence: File;
  note?: string;
}

// Sends the code that proves control of the claimed business email inbox
// (server/src/modules/directory-listing/service.ts sendClaimEmailVerificationCode).
// Always resolves - whether the address exists anywhere is never revealed.
export function useSendDirectoryClaimEmailCode(workspaceId: string | null) {
  return useMutation({
    mutationFn: (businessEmail: string) =>
      api.post<void>(workspacePath(workspaceId!, '/directory/claim-requests/verify-email'), { businessEmail }),
  });
}

// Confirms the code. The server records the verified state itself (keyed off
// the business email, not anything the client sends back) - see
// assertBusinessEmailVerified in service.ts - so createClaimRequest below
// still requires the same businessEmail to have just been confirmed.
export function useConfirmDirectoryClaimEmailCode(workspaceId: string | null) {
  return useMutation({
    mutationFn: ({ businessEmail, code }: { businessEmail: string; code: string }) =>
      api.post<{ verified: boolean }>(workspacePath(workspaceId!, '/directory/claim-requests/confirm-email'), { businessEmail, code }),
  });
}

export function useCreateDirectoryClaimRequest(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shopId, businessEmail, evidence, note }: CreateDirectoryClaimRequestInput) => {
      const formData = new FormData();
      formData.append('shopId', shopId);
      formData.append('businessEmail', businessEmail);
      formData.append('evidence', evidence);
      if (note) formData.append('note', note);
      return api.upload<{ id: string }>(workspacePath(workspaceId!, '/directory/claim-requests'), formData);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'directory-listing'] }),
  });
}

export function useUpdateDirectoryListing(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DirectoryListingUpdateInput) =>
      api.patch<{ listing: DirectoryListing }>(workspacePath(workspaceId!, '/directory/listing'), body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'directory-listing'] }),
  });
}

export interface DirectoryListingMediaUpload {
  shopId: string;
  kind: 'logo' | 'banner';
  path: string;
  url: string;
}

export function useUploadDirectoryListingMedia(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ field, file }: { field: 'logo' | 'banner'; file: File }) => {
      const formData = new FormData();
      formData.append(field, file);
      // The server answers with the stored object, not the listing row (see
      // UploadListingMediaResult in server/src/modules/directory-listing/
      // service.ts). The refreshed listing comes from the invalidate below,
      // so nothing here reads the body - but the type has to tell the truth
      // for whoever does next.
      return api.upload<DirectoryListingMediaUpload>(workspacePath(workspaceId!, '/directory/listing/media'), formData);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'directory-listing'] }),
  });
}
